import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loader2, Plus } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'
import AgendaItemEditor from './AgendaItemEditor'

interface Props {
  meetingId: string
  agendaItems: any[]
  users: { id: string; name: string }[]
  onSavingChange?: (saving: boolean) => void
  // MEETINGS.EDIT — addAgendaItem/removeAgendaItem/reorderAgendaItems (and
  // everything inside AgendaItemEditor/AgendaEntriesSection) are all EDIT.
  canEdit: boolean
  // MEETINGS.DELETE — pass-through for entries editor (see AgendaEntriesSection).
  canDelete: boolean
}

const AgendaListManager: React.FC<Props> = ({
  meetingId,
  agendaItems,
  users,
  onSavingChange,
  canEdit,
  canDelete,
}) => {
  const { t } = useTranslation()
  const [newAgendaTitle, setNewAgendaTitle] = useState('')
  const [structuralSaving, setStructuralSaving] = useState(false)
  const [deletingAgendaId, setDeletingAgendaId] = useState<string | null>(null)
  const [pendingItemSaves, setPendingItemSaves] = useState(0)

  const [addAgendaItem] = useMutation(Mutation.ADD_AGENDA_ITEM, {
    refetchQueries: ['meeting'],
  })
  const [removeAgendaItem] = useMutation(Mutation.REMOVE_AGENDA_ITEM, {
    refetchQueries: ['meeting'],
  })
  const [reorderAgendaItems] = useMutation(Mutation.REORDER_AGENDA_ITEMS, {
    refetchQueries: ['meeting'],
  })

  const isAnySaving = pendingItemSaves > 0 || structuralSaving

  useEffect(() => {
    onSavingChange?.(isAnySaving)
  }, [isAnySaving, onSavingChange])

  const handleItemSavingChange = useCallback((saving: boolean) => {
    setPendingItemSaves((n) => (saving ? n + 1 : Math.max(0, n - 1)))
  }, [])

  const handleAddAgenda = async () => {
    const title = newAgendaTitle.trim()
    if (!title || structuralSaving) return
    setStructuralSaving(true)
    try {
      const nextOrderIndex = agendaItems.length
      await addAgendaItem({
        variables: {
          meetingId,
          input: { title, responsibleId: null, orderIndex: nextOrderIndex },
        },
      })
      setNewAgendaTitle('')
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    } finally {
      setStructuralSaving(false)
    }
  }

  const handleMoveAgenda = async (idx: number, dir: -1 | 1) => {
    if (structuralSaving) return
    const target = idx + dir
    if (target < 0 || target >= agendaItems.length) return
    const ids = agendaItems.map((a: any) => a.id)
    const [moved] = ids.splice(idx, 1)
    ids.splice(target, 0, moved)
    setStructuralSaving(true)
    try {
      await reorderAgendaItems({ variables: { meetingId, itemIds: ids } })
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    } finally {
      setStructuralSaving(false)
    }
  }

  const handleRemoveAgenda = async (id: string, title: string) => {
    if (structuralSaving) return
    if (!window.confirm(t('Meeting_agenda_delete_confirm', { title }))) return
    setStructuralSaving(true)
    setDeletingAgendaId(id)
    try {
      await removeAgendaItem({ variables: { id } })
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    } finally {
      setStructuralSaving(false)
      setDeletingAgendaId(null)
    }
  }

  return (
    <div className="space-y-3">
      {agendaItems.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {t('Meeting_agenda_empty')}
        </p>
      ) : (
        agendaItems.map((a: any, idx: number) => (
          <AgendaItemEditor
            key={a.id}
            item={a}
            meetingId={meetingId}
            users={users}
            onSavingChange={handleItemSavingChange}
            canMoveUp={idx > 0}
            canMoveDown={idx < agendaItems.length - 1}
            onMoveUp={canEdit ? () => handleMoveAgenda(idx, -1) : undefined}
            onMoveDown={canEdit ? () => handleMoveAgenda(idx, 1) : undefined}
            onDelete={canEdit ? () => handleRemoveAgenda(a.id, a.title) : undefined}
            structuralSaving={structuralSaving}
            deleting={deletingAgendaId === a.id}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        ))
      )}

      {canEdit && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleAddAgenda()
          }}
          className="flex items-center gap-2 pt-2"
        >
          <Input
            value={newAgendaTitle}
            onChange={(e) => setNewAgendaTitle(e.target.value)}
            placeholder={t('Meeting_agenda_item_title')}
            disabled={structuralSaving}
            className="h-9"
          />
          <Button
            type="submit"
            size="sm"
            disabled={!newAgendaTitle.trim() || structuralSaving}
          >
            {structuralSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {t('Meeting_agenda_add')}
          </Button>
        </form>
      )}
    </div>
  )
}

export default AgendaListManager
